/**
 * Workspace tint provider (M10.5).
 *
 * Reads the active workspace's `profile.primary_color` and `accent_color`
 * and writes them onto the document root as CSS variables. Every glow,
 * hero background, and workspace mark cascades from these two variables,
 * giving each workspace its own visual personality without changing a
 * single component.
 */
import { useEffect } from "react";

import { useCurrentWorkspace } from "@/entities/workspace";

function hexToRGBA(hex: string, alpha: number): string {
  const m = hex.replace("#", "").match(/.{2}/g);
  if (!m) return `rgba(229,180,84,${alpha})`;
  const [r, g, b] = m.map((h) => parseInt(h, 16));
  return `rgba(${r},${g},${b},${alpha})`;
}

export function useWorkspaceTint(): void {
  const ws = useCurrentWorkspace();
  useEffect(() => {
    const root = document.documentElement.style;
    // Fallback to the signature amber if no workspace or no colours yet.
    const primary = ws?.profile?.primary_color || "#E5B454";
    const accent = ws?.profile?.accent_color || ws?.profile?.primary_color || "#F5C15C";
    root.setProperty("--ws-primary", primary);
    root.setProperty("--ws-accent", accent);
    root.setProperty("--ws-glow", hexToRGBA(primary, 0.14));
  }, [ws?.id, ws?.profile?.primary_color, ws?.profile?.accent_color]);
}
